import { useEffect, useRef, type ReactNode } from 'react'
import BottomSheetContent from '../../../../../../../components/BottomSheetComponents/BottomSheetContent'
import { SecondaryButton } from '../../../../../../../components/execution/SecondaryButton'
import { EventNames } from '../../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../../utils/appBridge'
import {
  PlansBottomSheet,
  PlansLumpsumSheetEventIds,
  PlansScreen
} from '../../../../analytics/plansEvents'
import { PlansPlanMode } from '../../../api/types'
import { PlanRecommendationSheetErrorState } from './PlanRecommendationSheetErrorState'

type PlanRecommendationSheetLayoutProps = {
  onDismiss: () => void
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
  planMode: PlansPlanMode.REBALANCE | PlansPlanMode.ALLOCATION
  children: ReactNode
  analyticsScreen?: string
  fundName?: string
  fundIsin?: string
}

export function PlanRecommendationSheetLayout({
  onDismiss,
  isLoading,
  isFetchFailed,
  onRetry,
  planMode,
  children,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation,
  fundName = '',
  fundIsin = ''
}: PlanRecommendationSheetLayoutProps) {
  const hasTrackedSheetLoad = useRef(false)

  useEffect(() => {
    if (isLoading || isFetchFailed || hasTrackedSheetLoad.current) {
      return
    }
    hasTrackedSheetLoad.current = true
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        ScreenName: analyticsScreen,
        eventId: PlansBottomSheet.LumpsumRecommendation,
        bottomSheetName: PlansBottomSheet.LumpsumRecommendation,
        ...(fundName ? { fundName } : {}),
        ...(fundIsin ? { fundIsin } : {})
      }
    })
  }, [analyticsScreen, fundIsin, fundName, isFetchFailed, isLoading])

  function handleClose() {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: analyticsScreen,
        eventId: PlansLumpsumSheetEventIds.Close,
        buttonLabel: 'Close',
        ...(fundName ? { fundName } : {}),
        ...(fundIsin ? { fundIsin } : {})
      }
    })
    onDismiss()
  }

  if (isFetchFailed) {
    return (
      <PlanRecommendationSheetErrorState
        planMode={planMode}
        onRetry={onRetry}
        onDismiss={onDismiss}
        analyticsScreen={analyticsScreen}
        fundName={fundName}
        fundIsin={fundIsin}
      />
    )
  }

  const bottomSheetBodyClassName = isLoading
    ? '!px-0 !pb-2 !overflow-y-auto no-scrollbar'
    : '!px-0 !pb-2 !overflow-visible'

  return (
    <BottomSheetContent
      onDismiss={handleClose}
      heading={undefined}
      bottomSheetHeadClassName="!pt-6"
      bottomSheetBodyClassName={bottomSheetBodyClassName}
      bottomSheetCtaWrapperClassName="z-10"
      bottomSheetCtas={
        <SecondaryButton
          label="Close"
          onClick={handleClose}
          secondaryButtonClassName="!h-[56px] !text-[16px]"
        />
      }
    >
      {children}
    </BottomSheetContent>
  )
}
